import { useLocation } from 'wouter';
import { useCallback, useEffect, useState } from 'react';

interface UseNestedTabStateOptions {
  parentTab: string;
  defaultSubTab?: string;
}

interface NestedTabState {
  selectedId: string | null;
  subTab: string;
  setSelectedId: (id: string | null) => void;
  setSubTab: (tab: string) => void;
  clearSelection: () => void;
}

/**
 * Extends useTabState to handle two-level nested tab state via URL hash.
 *
 * Hash format: #parentTab/selectedId/subTab
 * Examples:
 *   #facilities                      → list view (no selection)
 *   #facilities/fac-123/overview     → facility detail, Overview tab
 *   #facilities/fac-123/parties      → facility detail, Parties tab
 *
 * Listens to hashchange for browser Back/Forward support.
 * Mirrors state to sessionStorage for refresh resilience.
 */
export function useNestedTabState({
  parentTab,
  defaultSubTab = 'overview',
}: UseNestedTabStateOptions): NestedTabState {
  const [, setLocation] = useLocation();

  const sessionKey = (field: string) => `nestedTab:${parentTab}:${field}`;

  function parseCurrentHash(): { selectedId: string | null; subTab: string } {
    const raw = window.location.hash.replace(/^#/, '');
    const segments = raw.split('/');

    if (segments[0] !== parentTab) {
      // Hash belongs to a different tab — check sessionStorage fallback
      try {
        const storedId = sessionStorage.getItem(sessionKey('selectedId'));
        const storedTab = sessionStorage.getItem(sessionKey('subTab'));
        if (storedId) {
          return { selectedId: storedId, subTab: storedTab || defaultSubTab };
        }
      } catch {
        // sessionStorage unavailable
      }
      return { selectedId: null, subTab: defaultSubTab };
    }

    const selectedId = segments[1] || null;
    const subTab = segments[2] || defaultSubTab;
    return { selectedId, subTab };
  }

  const [selectedId, setSelectedIdState] = useState<string | null>(() => {
    return parseCurrentHash().selectedId;
  });

  const [subTab, setSubTabState] = useState<string>(() => {
    return parseCurrentHash().subTab;
  });

  function updateHash(id: string | null, tab: string) {
    const pathname = window.location.pathname;
    const search = window.location.search;
    if (id) {
      setLocation(`${pathname}${search}#${parentTab}/${id}/${tab}`, { replace: true });
    } else {
      setLocation(`${pathname}${search}#${parentTab}`, { replace: true });
    }
  }

  function writeSessionStorage(id: string | null, tab: string) {
    try {
      if (id) {
        sessionStorage.setItem(sessionKey('selectedId'), id);
        sessionStorage.setItem(sessionKey('subTab'), tab);
      } else {
        sessionStorage.removeItem(sessionKey('selectedId'));
        sessionStorage.removeItem(sessionKey('subTab'));
      }
    } catch {
      // sessionStorage unavailable (privacy mode)
    }
  }

  // Listen for hashchange (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const parsed = parseCurrentHash();
      setSelectedIdState(parsed.selectedId);
      setSubTabState(parsed.subTab);
      writeSessionStorage(parsed.selectedId, parsed.subTab);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [parentTab, defaultSubTab]);

  const setSelectedId = useCallback(
    (id: string | null) => {
      const tab = id ? defaultSubTab : subTab;
      setSelectedIdState(id);
      setSubTabState(id ? defaultSubTab : subTab);
      updateHash(id, tab);
      writeSessionStorage(id, tab);
    },
    [parentTab, defaultSubTab, subTab, setLocation],
  );

  const setSubTab = useCallback(
    (tab: string) => {
      setSubTabState(tab);
      if (selectedId) {
        updateHash(selectedId, tab);
        writeSessionStorage(selectedId, tab);
      }
    },
    [parentTab, selectedId, setLocation],
  );

  const clearSelection = useCallback(() => {
    setSelectedIdState(null);
    setSubTabState(defaultSubTab);
    updateHash(null, defaultSubTab);
    writeSessionStorage(null, defaultSubTab);
  }, [parentTab, defaultSubTab, setLocation]);

  return { selectedId, subTab, setSelectedId, setSubTab, clearSelection };
}
